import { action, observable, makeObservable } from 'mobx';
import axios from 'axios';
import _ from 'lodash';
import { userNewsStatus } from '../utils/constants';
import { errorAlert, successAlert } from '../utils/alerts';

const { REACT_APP_API_URL } = process.env;

class NewsletterStore {
  lists = null;
  subscriptionDetails = null;
  interests = null;
  language = null;
  listsLoading = null;
  detailsLoading = null;
  newsletterError = null;
  newsletterSuccess = null;
  showNewslettersDisabled = false;
  showAllCommsDisabled = false;
  isUserUnsubscribed = false;
  userSubscriptionStatus = null;
  isUserSubscribed = false;

  constructor(profileStore) {
    makeObservable(this, {
      lists: observable,
      subscriptionDetails: observable,
      interests: observable,
      language: observable,
      listsLoading: observable,
      detailsLoading: observable,
      newsletterError: observable,
      newsletterSuccess: observable,
      showNewslettersDisabled: observable,
      showAllCommsDisabled: observable,
      isUserUnsubscribed: observable,
      userSubscriptionStatus: observable,
      isUserSubscribed: observable,
      subscribeUser: action,
      unsubscribeUser: action,
      getLists: action,
      getSubscriptionDetails: action,
      savePreferences: action,
      clearLists: action,
      clearInfos: action.bound,
      clearAlerts: action
    });

    this.profileStore = profileStore;
  }

  subscribeUser = async () => {
    const authToken = await this.profileStore.getAuthToken();
    const result = await axios.post(
      `${REACT_APP_API_URL}/newsletter/subscribe`,
      { user: this.profileStore.user },
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );

    return result.data;
  };

  unsubscribeUser = async () => {
    const authToken = await this.profileStore.getAuthToken();
    const result = await axios.post(
      `${REACT_APP_API_URL}/newsletter/unsubscribe`,
      { user: this.profileStore.user },
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );

    return result.data;
  };

  getLists = async () => {
    const authToken = await this.profileStore.getAuthToken();

    this.listsLoading = true;

    try {
      const result = await axios.get(`${REACT_APP_API_URL}/newsletter/lists`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      this.listsLoading = false;
      this.lists = result.data;

      return result.data;
    } catch (error) {
      errorAlert(error);
      return false;
    }
  };

  async getSubscriptionDetails() {
    const authToken = await this.profileStore.getAuthToken();

    this.detailsLoading = true;
    const lists = await this.getLists();

    try {
      const result = await axios.get(`${REACT_APP_API_URL}/newsletter/subscription-details`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      this.isUserSubscribed = result.data.status === userNewsStatus.SUBSCRIBED;
      this.userSubscriptionStatus = result.data.status;
      this.detailsLoading = false;

      const interests = [];
      _.forEach(lists, listItem => {
        interests.push({
          ...listItem,
          interested: result.data.status === userNewsStatus.UNSUBSCRIBED ? false : result.data.interests[listItem.id],
        });
      });
      this.interests = interests;
      this.language = result.data.language || this.profileStore.profileData.language;
      this.subscriptionDetails = result.data;
    } catch (error) {
      errorAlert(error);
      // return false
    }
  }

  savePreferences = async ({ interests, marketingUpdates, language }, setFormSubmitting, t) => {
    const authToken = await this.profileStore.getAuthToken();
    // const currentProfileData = toJS(this.profileStore.profileData)

    try {
      setFormSubmitting(true);

      // if all interests are switched off
      if (interests.every(i => !i.interested)) {
        marketingUpdates = false;
      }

      await this.profileStore.saveProfile(
        {
          marketingUpdates,
        },
        t,
        true
      );
      // if (currentProfileData.marketingUpdates !== marketingUpdates) {
      if (marketingUpdates) {
        this.showAllCommsDisabled = false;
        !this.isUserSubscribed && (await this.subscribeUser());
      } else {
        this.showAllCommsDisabled = true;
        await this.unsubscribeUser();
      }
      // }

      const interestsHash = interests.reduce((acc, item) => {
        acc[item.id] = item.interested;
        return acc;
      }, {});

      this.showNewslettersDisabled = _.every(interestsHash, i => i === false);
      if (this.showAllCommsDisabled) {
        this.showNewslettersDisabled = false;
      }
      await axios.post(
        `${REACT_APP_API_URL}/newsletter/set-interests`,
        { user: this.profileStore.user, interests: interestsHash, language },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      await this.getSubscriptionDetails();

      setFormSubmitting(false);

      setTimeout(() => {
        this.clearAlerts();
        this.clearInfos();
        setFormSubmitting(false);
      }, 5000);

      successAlert(t('newsletters.updateSuccess'));
    } catch (error) {
      errorAlert(t('newsletters.updateError'));
      return false;
    }
  };

  clearLists() {
    this.lists = null;
    this.interests = null;
    this.language = null;
  }

  clearInfos() {
    this.showNewslettersDisabled = false;
    this.showAllCommsDisabled = false;
  }

  clearAlerts() {
    this.newsletterError = null;
    this.newsletterSuccess = null;
  }
}

export default NewsletterStore;
